/**
 * ReportIssuePage - Customer-facing issue reporting wizard
 * Routes complaints to the provider (middle-party model)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import {
  AlertCircle, Clock, XCircle, MessageSquare, Upload, ChevronRight,
  Check, ArrowLeft, Loader2, Camera, FileText, HelpCircle, RefreshCw,
  DollarSign, Calendar
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Issue types with icons and descriptions
const ISSUE_TYPES = [
  {
    id: 'service_quality',
    name: 'Service Quality',
    icon: AlertCircle,
    description: 'The service did not meet expectations',
    examples: ['Poor quality food', 'Unprofessional DJ', 'Venue not as described']
  },
  {
    id: 'late_arrival',
    name: 'Late Arrival / No Show',
    icon: Clock,
    description: 'Provider arrived late or did not show up',
    examples: ['Caterer arrived 2 hours late', 'Photographer missed ceremony']
  },
  {
    id: 'wrong_order',
    name: 'Wrong Order / Missing Items',
    icon: XCircle,
    description: 'Received wrong items or items were missing',
    examples: ['Wrong menu served', 'Equipment missing', 'Less staff than promised']
  },
  {
    id: 'damage',
    name: 'Damage / Safety Issue',
    icon: AlertCircle,
    description: 'Property damage or safety concerns',
    examples: ['Equipment damaged venue', 'Food safety issue', 'Electrical hazard']
  },
  {
    id: 'communication',
    name: 'Communication Issue',
    icon: MessageSquare,
    description: 'Provider was unresponsive or rude',
    examples: ['No responses to messages', 'Rude behavior', 'Last-minute changes']
  },
  {
    id: 'other',
    name: 'Other Issue',
    icon: HelpCircle,
    description: 'Other problems not listed above',
    examples: []
  }
];

// Resolution preferences
const RESOLUTION_OPTIONS = [
  {
    id: 'reschedule',
    name: 'Reschedule Service',
    icon: Calendar,
    description: 'Request to reschedule for another date'
  },
  {
    id: 'partial_refund',
    name: 'Partial Refund',
    icon: DollarSign,
    description: 'Request partial compensation'
  },
  {
    id: 'full_refund',
    name: 'Full Refund',
    icon: DollarSign,
    description: 'Request complete refund'
  },
  {
    id: 'redo_service',
    name: 'Redo Service',
    icon: RefreshCw,
    description: 'Request the provider to redo the service'
  },
  {
    id: 'apology',
    name: 'Acknowledgment & Apology',
    icon: MessageSquare,
    description: 'Just want an explanation and apology'
  }
];

export default function ReportIssuePage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [fetchingBooking, setFetchingBooking] = useState(true);
  
  // Form state
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [preferredResolution, setPreferredResolution] = useState('');
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  
  // Upload state
  const [uploading, setUploading] = useState(false);

  // Fetch booking details
  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setFetchingBooking(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/events/bookings?booking_id=${bookingId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.bookings?.length > 0) {
            setBooking(data.bookings[0]);
            // Pre-fill contact info from booking
            if (data.bookings[0].contact) {
              setContactInfo(data.bookings[0].contact);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch booking:', error);
      } finally {
        setFetchingBooking(false);
      }
    };
    
    fetchBooking();
  }, [bookingId]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setUploading(true);
    try {
      // For demo, just add placeholder URLs
      const newUrls = Array.from(files).map((_, i) => 
        `https://placeholder.com/evidence-${Date.now()}-${i}.jpg`
      );
      setEvidenceUrls(prev => [...prev, ...newUrls]);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  // Submit issue
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const issueData = {
        booking_id: bookingId || 'manual-report',
        issue_type: issueType,
        description,
        preferred_resolution: preferredResolution,
        evidence_urls: evidenceUrls,
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/issues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(issueData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Issue reported successfully! The provider will respond within 24-48 hours.');
        setStep(5); // Success step
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">What type of issue are you reporting?</h3>
            <p className="text-sm text-gray-500">Select the category that best describes your problem</p>
            
            <div className="grid gap-3">
              {ISSUE_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = issueType === type.id;
                
                return (
                  <button
                    key={type.id}
                    onClick={() => setIssueType(type.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-red-300'
                    }`}
                    data-testid={`issue-type-${type.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-red-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-red-600' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{type.name}</p>
                        <p className="text-sm text-gray-500">{type.description}</p>
                        {type.examples.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            e.g., {type.examples.slice(0, 2).join(', ')}
                          </p>
                        )}
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-red-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Describe the issue</h3>
            <p className="text-sm text-gray-500">Provide details about what happened</p>
            
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe what happened in detail. Include specific information like times, names, and exactly what went wrong..."
              rows={6}
              className="resize-none"
              data-testid="issue-description"
            />
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> The more detail you provide, the better the provider can understand and resolve your issue.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Upload Evidence (Optional)</h3>
            <p className="text-sm text-gray-500">Photos or videos can help support your complaint</p>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileUpload}
                className="hidden"
                id="evidence-upload"
              />
              <label 
                htmlFor="evidence-upload"
                className="cursor-pointer"
              >
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700">Click to upload photos/videos</p>
                <p className="text-sm text-gray-500">Up to 5 files, max 10MB each</p>
              </label>
              {uploading && <Loader2 className="w-6 h-6 animate-spin mx-auto mt-3" />}
            </div>
            
            {evidenceUrls.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {evidenceUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <button
                      onClick={() => setEvidenceUrls(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">What resolution would you prefer?</h3>
            <p className="text-sm text-gray-500">Let the provider know how you'd like this resolved</p>
            
            <RadioGroup value={preferredResolution} onValueChange={setPreferredResolution}>
              {RESOLUTION_OPTIONS.map(option => {
                const Icon = option.icon;
                return (
                  <div key={option.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value={option.id} id={option.id} />
                    <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{option.name}</span>
                      </div>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Your Contact Information</h4>
              <div className="space-y-3">
                <Input
                  placeholder="Your name"
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
                />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
                />
                <Input
                  placeholder="Phone number"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Your complaint will be sent directly to the provider. 
                They are required to respond within 24-48 hours.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Issue Reported Successfully</h3>
            <p className="text-gray-600 mb-6">
              Your complaint has been sent to the provider. They will respond within 24-48 hours.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-medium mb-2">What happens next?</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">1</span>
                  <span>The provider will review your complaint</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">2</span>
                  <span>They will offer a resolution (refund, reschedule, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">3</span>
                  <span>You can accept or discuss the resolution</span>
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/events')}>
                Back to Events
              </Button>
              <Button onClick={() => navigate('/my-bookings')}>
                View My Bookings
              </Button>
            </div>
          </div>
        );
    }
  };

  if (fetchingBooking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Report an Issue</h1>
              <p className="text-red-100">We're sorry you had a problem. Let's get it resolved.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Booking Info */}
        {booking && step < 5 && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Booking #{booking.id?.slice(0, 8)}</p>
                  <p className="text-sm text-gray-500">
                    {booking.event_date && format(new Date(booking.event_date), 'MMM d, yyyy')} | {booking.event_type}
                  </p>
                </div>
                <Badge variant="outline">{booking.booking_type}</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        {step < 5 && (
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s < step ? 'bg-red-600 text-white' :
                  s === step ? 'bg-red-100 text-red-700 border-2 border-red-600' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`flex-1 h-1 ${s < step ? 'bg-red-600' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step Content */}
        <Card>
          <CardContent className="pt-6">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        {step < 5 && (
          <div className="flex justify-between mt-6">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>
                Back
              </Button>
            ) : <div />}
            
            {step < 4 ? (
              <Button 
                onClick={() => setStep(s => s + 1)}
                disabled={
                  (step === 1 && !issueType) ||
                  (step === 2 && !description)
                }
                className="bg-red-600 hover:bg-red-700"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading || !preferredResolution || !contactInfo.name || !contactInfo.email}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit Issue
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
